import * as Yup from 'yup';
// @ts-ignore
import { globalTranslator } from "lib/GlobalTranslator";
// @ts-ignore
import { DateTime } from "luxon";

const t = globalTranslator;
export interface HistoricalComparisonChartProps {
    chartType: string;
    accountId?: number | null;
    accountNumber?: string;
    startDate: DateTime;
    endDate: DateTime;
    geoCoordinates: {
        latitude: number,
        longitude: number
    }[],
    selectedMeter: string;
    setDateRangeEnd: Function;
    setDateRangeStart: Function;
    showEST: boolean;
    uom: string;
    yearOne: number;
    yearTwo: number;
    setCsvDataToDownload?: any;
};

const updateHistoricalUsageWithWeather = (swrWeatherHistorySummary: any, swrHistoricalUsage: any, yearOne: number, yearTwo: number, chartType: string) => {
    if (chartType === "month") {
        const yearOneData = swrWeatherHistorySummary?.result
            .filter((item: any) => item.location.year === yearOne)
            .map((item: any) => item.location.values)
            .flat();

        const yearTwoData = swrWeatherHistorySummary?.result
            .filter((item: any) => item.location.year === yearTwo)
            .map((item: any) => item.location.values)
            .flat();

        const updatedHistoricalUsage = swrHistoricalUsage?.result?.results?.map((usage: any) => {
            const billingDate = new Date(usage.startDate);
            const billingMonth = billingDate.getUTCMonth() + 1;

            const matchingWeatherDataYearOne = yearOneData.find((weather: any) => {
                const weatherDate = new Date(weather.datetime);
                const weatherMonth = weatherDate.getUTCMonth() + 1;
                return billingMonth === weatherMonth;
            });

            const matchingWeatherDataYearTwo = yearTwoData.find((weather: any) => {
                const weatherDate = new Date(weather.datetime);
                const weatherMonth = weatherDate.getUTCMonth() + 1;
                return billingMonth === weatherMonth;
            });

            if (matchingWeatherDataYearOne || matchingWeatherDataYearTwo) {
                return {
                    ...usage,
                    weatherDataYearOne: matchingWeatherDataYearOne?.temp,
                    weatherDataYearTwo: matchingWeatherDataYearTwo?.temp,
                    precipDataYearOne: matchingWeatherDataYearOne?.precip,
                    precipDataYearTwo: matchingWeatherDataYearTwo?.precip
                };
            }

            return usage;
        });

        return updatedHistoricalUsage;
    }
    else if (chartType === "day") {     
        const updatedHistoricalUsage = swrHistoricalUsage?.result?.results?.map((usage: any) => {
            const yearOneDate = DateTime.fromISO(usage.yearOneDate);
            const yearTwoDate = DateTime.fromISO(usage.yearTwoDate);

            const matchingWeatherDataYearOne = swrWeatherHistorySummary?.result?.days.find((weather: any) => {
                const weatherDate = DateTime.fromISO(weather.dateTime);
                return weatherDate.month === yearOneDate.month &&
                    weatherDate.day === yearOneDate.day &&
                    weatherDate.year === yearOneDate.year;
            });

            const matchingWeatherDataYearTwo = swrWeatherHistorySummary?.result?.days.find((weather: any) => {
                const weatherDate = DateTime.fromISO(weather.dateTime);
                return weatherDate.month === yearTwoDate.month &&
                    weatherDate.day === yearTwoDate.day && 
                    weatherDate.year === yearTwoDate.year;
            });

            if (matchingWeatherDataYearOne || matchingWeatherDataYearTwo) {
                return {
                    ...usage,
                    weatherDataYearOne: matchingWeatherDataYearOne?.temp,
                    weatherDataYearTwo: matchingWeatherDataYearTwo?.temp,
                    precipDataYearOne: matchingWeatherDataYearOne?.precip,
                    precipDataYearTwo: matchingWeatherDataYearTwo?.precip
                };
            }

            return usage;
        });

        return updatedHistoricalUsage;
    }
}

const historicalComparisonValidationSchema = Yup.object().shape({
    chartType: Yup.string().required(),
    yearOne: Yup.date()
        .required(t("Required")),
    yearTwo: Yup.date()
        .required(t("Required"))
        .test("different-years", t("'Year One' and 'Year Two' must not be the same year."), function(value: any) {
            const { yearOne } = this.parent;
            if (!yearOne || !value) return true;
            const yearOneValue = new Date(yearOne).getFullYear();
            const yearTwoValue = new Date(value).getFullYear();

            return yearOneValue !== yearTwoValue;
        }),
    startMonth: Yup.date()
        .when('chartType', {
            is: 'month',
            then: (schema) => schema.required(t("Required")), //NOSONAR
            otherwise: (schema) => schema.nullable().transform((curr, orig) => orig === '' ? null : curr)
        }),
    endMonth: Yup.date()
        .when('chartType', {
            is: 'month',
            then: (schema) => schema //NOSONAR
                .required(t("Required"))
                .test("is-greater-or-equal", t("End Month must be later than or equal to Start Month."), function (value: any) {
                    const { startMonth } = this.parent;         
                    if (!startMonth || !value) return true;

                    const startMonthValue = new Date(startMonth).getMonth(); 
                    const endMonthValue = new Date(value).getMonth();

                    return endMonthValue >= startMonthValue;
                }),
            otherwise: (schema) => schema.nullable().transform((curr, orig) => orig === '' ? null : curr)
        }),
    startDate: Yup.date()
        .when('chartType', {
            is: 'day',
            then: (schema) => schema.required(t("Required")), //NOSONAR
            otherwise: (schema) => schema.nullable().transform((curr, orig) => orig === '' ? null : curr)
        }),
    endDate: Yup.date()
        .when('chartType', {
            is: 'day',
            then: (schema) => schema //NOSONAR
                .required(t("Required"))
                .test('date-order', t('End Date must be later than or equal to Start Date'), function(endDate) {
                    const { startDate } = this.parent;
                    if (!startDate || !endDate) return true;
                    
                    const start = DateTime.fromJSDate(startDate);
                    const end = DateTime.fromJSDate(endDate);
                    
                    // Allow December to January transitions
                    if (start.month === 12 && end.month === 1) {
                        return true;
                    }
                    
                    // Compare dates ignoring year
                    const startDay = start.toFormat('MM-dd');
                    const endDay = end.toFormat('MM-dd');
                    return endDay >= startDay;
                })
                .test('max-range', t('Date range cannot exceed two weeks'), function(endDate) {
                    const { startDate } = this.parent;
                    if (!startDate || !endDate) return true;
                    
                    const start = DateTime.fromJSDate(startDate);
                    const end = DateTime.fromJSDate(endDate);
                    
                    // Set both to same year for comparison
                    let startCompare = start.set({ year: 2025 });
                    let endCompare = end.set({ year: 2025 });

                    // If December to January, add a year to end date
                    if (start.month === 12 && end.month === 1) {
                        endCompare = endCompare.set({ year: 2026 });
                    }
                    
                    // Calculate days between dates
                    const diffInDays = Math.abs(endCompare.diff(startCompare, 'days').days);

                    return diffInDays <= 15;
                }),
            otherwise: (schema) => schema.nullable().transform((curr, orig) => orig === '' ? null : curr)
        })
});

// if the start/end date is later than todays date, we still want to show all of our meters in the meter select dropdown
const getDateRangesForMeterCheck = (startFilter: DateTime, endFilter: DateTime, yearOne: number, yearTwo: number, timezone: string) => {
    if (!startFilter || !endFilter || !yearOne || !yearTwo) {
        return null;
    }
    
    const startTemplate = startFilter.toObject();
    const endTemplate = endFilter.toObject();

    // Create both ranges
    const firstRange = {
        start: DateTime.fromObject({ ...startTemplate, year: yearOne }, { zone: timezone }),
        end: DateTime.fromObject({ ...endTemplate, year: yearOne }, { zone: timezone })
    };

    const secondRange = {
        start: DateTime.fromObject({ ...startTemplate, year: yearTwo }, { zone: timezone }),
        end: DateTime.fromObject({ ...endTemplate, year: yearTwo }, { zone: timezone })
    };

    // Return ranges with earlier year first
    return yearOne < yearTwo ? { rangeOne: firstRange, rangeTwo: secondRange } : { rangeOne: secondRange, rangeTwo: firstRange };
};

export {
    updateHistoricalUsageWithWeather,
    historicalComparisonValidationSchema,
    getDateRangesForMeterCheck
}